import { cn } from '@/lib/utils'

/*
  Class helpers for the console's ported CSS (styles/console.css), for the rare
  element that is not one of the components here -- a react-router <Link> styled
  as a button, a card that is also a grid cell.
*/

/** `.btn--*` in console.css. `default` is the bare `.btn` (transparent, raise on hover). */
export type ButtonVariant = 'default' | 'primary' | 'accent' | 'outline' | 'neutral' | 'quiet'
/** 28px (`xs`), 34px (`md`, the default) or 40px (`lg`). */
export type ButtonSize = 'xs' | 'md' | 'lg'

export interface ButtonLook {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Square, icon only (`.btn--icon`). Give it an `aria-label`. */
  iconOnly?: boolean
}

/** The class string for a console button. */
export function buttonClass({ variant = 'default', size = 'md', iconOnly }: ButtonLook, className?: string) {
  return cn(
    'btn',
    variant !== 'default' && `btn--${variant}`,
    size !== 'md' && `btn--${size}`,
    iconOnly && 'btn--icon',
    className,
  )
}

/** Column spans the prototype uses (`.col-N` there, `.ccol-N` here). They collapse on narrow screens. */
export type ColSpan = 3 | 4 | 6 | 8 | 12

/** The class for a span, for an element that is itself the grid item: `cn('card', colClass(4))`. */
export function colClass(span: ColSpan) {
  return `ccol-${span}`
}

/** The design's own words for something not built yet (Settings → Notifications, SMS). */
export const COMING_SOON = 'Coming soon'

/** "Coming soon · <why>", the shape of the design's `.info` title. */
export function comingSoonTitle(reason?: string) {
  return reason ? `${COMING_SOON} · ${reason}` : COMING_SOON
}

/**
 * Props that make any control -- a nav item, a menu row -- a coming-soon one:
 * spread them on it. It looks disabled (`.soon`, the design's opacity .5), does
 * nothing, and stays focusable so its title (the reason) can be read. For a
 * `.btn`, use `<ComingSoonButton>`.
 */
export function comingSoonProps(reason?: string, className?: string) {
  return {
    'aria-disabled': true as const,
    title: comingSoonTitle(reason),
    className: cn('soon', className),
    onClick: (event: { preventDefault: () => void }) => event.preventDefault(),
  }
}
